// ** React Imports
import { useState, useEffect, ChangeEvent, useCallback } from 'react'

// ** Type Imports
import { GoogleAdsTagsType, GoogleAdsType, LimechatType, LimechatAnalyticsType } from 'src/lib/types/integrations'

// ** Child Imports
import { Checkbox, Switch, Grid, Typography, TextField } from '@mui/material'
import ConfirmationDialog from 'src/components/common/feedback-and-dialog/ConfirmationDialog'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import { Check, Edit, PlusSquare, Trash2 } from 'react-feather'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'
import { LoadingButton } from '@mui/lab'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialLimechatAnalyticsState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { useLocale } from 'src/lib/hooks/useLocal'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { parseLimechatPayload } from 'src/lib/utils/parsers'
import { getDefaultChannels } from 'src/lib/utils/helpers'
interface LimechattrackingFormProps {
  limechat: LimechatType
  index: number
  limechatAnalytics: LimechatAnalyticsType
  setLimechatAnalytics: React.Dispatch<React.SetStateAction<LimechatAnalyticsType>>
}

const LimechatTrackingForm: React.FC<LimechattrackingFormProps> = ({
  limechat,
  index,
  limechatAnalytics,
  setLimechatAnalytics
}) => {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false)
  const [isVerfiying, setIsVerifying] = useState<boolean>(false)
  const removeLimechat = () => {
    const modifiedLimechat = [...limechatAnalytics?.limechat]
    modifiedLimechat.splice(index, 1)
    setLimechatAnalytics({
      ...limechatAnalytics,
      limechat: modifiedLimechat
    })
  }

  return (
    <>
      <IntegrationChannels
        channels={limechat.channels ?? []}
        onChange={(channels: any) => {
          const newLimeChat: LimechatType = { ...limechat }
          newLimeChat.channels = channels
          const modifiedLimechat = [...limechatAnalytics?.limechat]
          modifiedLimechat[index] = newLimeChat
          setLimechatAnalytics({ ...limechatAnalytics, limechat: modifiedLimechat })
        }}
        hideChannels={!limechat?.enabled}
      >
        <>
          <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  Limechat Configuration
                </Typography>
                <div className='flex justify-start gap-2'>
                  <div className='flex items-center justify-between w-full'>
                    <button
                      className='flex items-center text-sm font-semibold bg-white text-coal-dark w-fit'
                      onClick={() => setIsDeleteConfirmOpen(!isDeleteConfirmOpen)}
                    >
                      <Trash2 size='16' />
                    </button>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={12} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography>Account Id</Typography>
                <Grid xs={9} md={9}>
                  <div className=''>
                    <InputTextField
                      inputProps={{
                        style: {
                          marginLeft: -16,
                          marginRight: -16,
                          paddingLeft: 14,
                          paddingRight: 14,
                          width: '104%',
                          fontSize: 14
                        },
                        'aria-label': 'controlled'
                      }}
                      textFieldId='account-id'
                      inputType='text'
                      inputPlaceholder='Account Id'
                      inputValue={limechat?.['x-fb-account-id'] || ''}
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const newValue = event.target.value
                        const newLimechat = { ...limechat, ['x-fb-account-id']: newValue }
                        const modifiedLimechat = [...(limechatAnalytics?.limechat || [])]
                        modifiedLimechat[index] = newLimechat
                        setLimechatAnalytics({
                          ...limechatAnalytics,
                          limechat: modifiedLimechat
                        })
                      }}
                      errorStatus={false}
                      errorMessage={''}
                    />
                  </div>
                </Grid>
              </Grid>

              <Grid item xs={12} md={12} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography>Access Token</Typography>
                <Grid xs={9} md={9}>
                  <div className=''>
                    <InputTextField
                      inputProps={{
                        style: {
                          marginLeft: -16,
                          marginRight: -16,
                          paddingLeft: 14,
                          paddingRight: 14,
                          width: '104%',
                          fontSize: 14
                        },
                        'aria-label': 'controlled'
                      }}
                      textFieldId='access-token'
                      inputType='password'
                      inputPlaceholder='Access token'
                      inputValue={limechat?.['x-limechat-uat'] || ''}
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const newValue = event.target.value
                        const newLimechat = { ...limechat, ['x-limechat-uat']: newValue }
                        const modifiedLimechat = [...(limechatAnalytics?.limechat || [])]
                        modifiedLimechat[index] = newLimechat
                        setLimechatAnalytics({
                          ...limechatAnalytics,
                          limechat: modifiedLimechat
                        })
                      }}
                      errorStatus={false}
                      errorMessage={''}
                    />
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid display={'flex'} flexDirection={'column'} sx={{ gap: 2, width: '100%' }}>
            <Grid item xs={12} display={'flex'} alignItems={'center'}>
              <Grid container>
                <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
                  <Typography variant='body2'>Limechat Configuration is </Typography>
                  <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                    {`${limechat?.enabled ? 'enabled' : 'disabled'}`}
                  </Typography>
                </Grid>

                <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
                  <Switch
                    name='LimechatToggle'
                    checked={limechat?.enabled}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const value = event?.target?.checked
                      const newLimechat = { ...limechat }
                      newLimechat.enabled = value
                      const modifiedLimechat = [...limechatAnalytics?.limechat]
                      modifiedLimechat[index] = newLimechat
                      setLimechatAnalytics({
                        ...limechatAnalytics,
                        limechat: modifiedLimechat
                      })
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      </IntegrationChannels>

      {isDeleteConfirmOpen ? (
        <ConfirmationDialog
          jobButton={'Yes, remove'}
          cancelButton={'No'}
          jobButtonColor='error'
          cancelButtonColor='secondary'
          confirmationText={'Are you sure you want to remove the limechat configuration?'}
          title={'Remove limechat configuration'}
          isOpen={isDeleteConfirmOpen}
          setIsConfirmationOpen={setIsDeleteConfirmOpen}
          handleJob={removeLimechat}
        />
      ) : (
        <></>
      )}
    </>
  )
}

interface LimechatProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const Limechat: React.FC<LimechatProps> = ({ isOnboarding = false, handleOnboardingSave }) => {
  // Context hook
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { t } = useLocale()
  const { limechat } = integrations || {}

  //Local State
  const [limechatAnalytics, setLimechatAnalytics] = useState<LimechatAnalyticsType>(initialLimechatAnalyticsState)
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary) return
    setLimechatAnalytics(temporary.integrations?.limechat)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(limechat) !== JSON.stringify(limechatAnalytics)))
  }, [limechatAnalytics, limechat])

  useEffect(() => {
    if (!limechat) return
    setLimechatAnalytics(limechat)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [limechat, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            limechat: limechatAnalytics
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
        return
      }
      setIntegrations(response?.data)
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const isValidForm = useCallback(() => {
    if (limechatAnalytics?.enabled) {
      let message = ''
      if (!limechatAnalytics?.limechat.length) {
        message = 'Please add limechat configurations to enable tracking'
      }
      if (
        !limechatAnalytics?.limechat.every(
          (limechatConfig: LimechatType) => limechatConfig?.['x-fb-account-id'] && limechatConfig?.['x-limechat-uat']
        )
      ) {
        message = 'Please fill in all the required fields for each limechat configuration'
      }
      if (message) {
        errorToast(message)
        return false
      }
    }
    return true
  }, [limechatAnalytics])

  const callSubmit = async () => {
    if (!isValidForm()) return
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const payload = {
        enabled: limechatAnalytics?.enabled,
        limechat: parseLimechatPayload(limechatAnalytics?.limechat)
      }
      const response: any = await setAnalyticsConfig('limechat', payload)
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations({ ...integrations, limechat: limechatAnalytics }, false)
        successToast('Successfully saved analytics configuration!')
        Boolean(handleOnboardingSave) && handleOnboardingSave && handleOnboardingSave()
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errorMessage
        ? error?.response?.data?.errorMessage
        : `Could not save configuration!`
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      console.error(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleDiscardSaving = () => {
    if (!limechat) return
    setLimechatAnalytics(limechat)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const onAddLimechatBtnClick = async () => {
    const defaultChannels = await getDefaultChannels()
    const modifiedLimechat = [...limechatAnalytics?.limechat]
    modifiedLimechat.push({
      enabled: false,
      channels: defaultChannels
    })
    setLimechatAnalytics({
      ...limechatAnalytics,
      limechat: modifiedLimechat
    })
  }

  const handleAlerts = (severity: string, message?: string) => {
    errorToast(message)
  }
  return (
    <>
      <LayoutContainer>
        <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
          <Grid container>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>Limechat Tracking is</Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${limechatAnalytics?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='limechatToggle'
                checked={limechatAnalytics?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const checked = event?.target?.checked
                  setLimechatAnalytics({ ...limechatAnalytics, enabled: checked })
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </LayoutContainer>
      {limechatAnalytics.limechat?.map((tag: any, index: number) => (
        <LimechatTrackingForm
          key={index}
          limechat={tag}
          index={index}
          setLimechatAnalytics={setLimechatAnalytics}
          limechatAnalytics={limechatAnalytics}
        />
      ))}
      <div className='flex flex-wrap gap-4'>
        <button
          className='flex items-center gap-1 px-3 py-2 text-sm font-semibold bg-white border rounded-lg border-flo-gray-light text-coal-dark w-fit'
          onClick={onAddLimechatBtnClick}
        >
          <PlusSquare size='16' />
          Add Limechat Configuration
        </button>
      </div>
      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={callSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <button
          className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
          onClick={callSubmit}
        >
          {t('save')}
        </button>
      )}
    </>
  )
}
export default Limechat
